import type {
  Clubhouse_SimulatorResponseDto,
  FacilityInstalledUnityGames,
  SimulatorDto,
} from "Models";
import type { AppQueryConfig } from "QueryTypes";
import { normalize } from "normalizr";
import { facilityAndSimSchema } from "../facility/facilitySchema";
import type { SimulatorsEntitiesState } from "StoreTypes";
import { clubhouseApiHeadersV2 } from "utils/api/apiHelper";
import { config } from "@/config/env";

const urls = {
  installedUnityGames(facilityId: string): string {
    return `${config.apiBaseUrl}/api/unity/facility/${facilityId}/installed_games`;
  },
  updateFacilitySimulators(facilityId: string): string {
    return `${config.apiBaseUrl}/api/facilities/${facilityId}/simulators`;
  },
  updateFacilitySimulatorName(facilityId: string): string {
    return `${config.apiBaseUrl}/api/facilities/${facilityId}/simulators/names`;
  },
  updateSimulatorScreenshots(facilityId: string): string {
    return `${config.apiBaseUrl}/api/facilities/${facilityId}/simulators/screenshots`;
  },
  updateSimulatorTerrainEffects(facilityId: string): string {
    return `${config.apiBaseUrl}/api/facilities/${facilityId}/simulators/terraineffects`;
  },
};

export const facilityInstalledUnityGamesQuery = (
  facilityId: string,
): AppQueryConfig => ({
  url: urls.installedUnityGames(facilityId),
  queryKey: `facilityInstalledUnityGamesQuery:${facilityId}`,
  options: {
    ...clubhouseApiHeadersV2(),
    method: "GET",
  },
  transform: (responseJson: FacilityInstalledUnityGames) => {
    return {
      installedUnityGames: {
        byFacilityId: {
          [facilityId]: responseJson,
        },
      },
    };
  },
  update: {
    installedUnityGames: (oldValue, newValue) => ({
      ...oldValue,
      byFacilityId: {
        ...oldValue?.byFacilityId,
        ...{
          [facilityId]: {
            ...oldValue?.byFacilityId?.[facilityId],
            ...newValue.byFacilityId[facilityId],
          },
        },
      },
    }),
  },
});

export const updateFacilitySimulatorsOrderQuery = (
  facilityId: string,
  body: {
    id: string;
    name: string;
  }[],
): AppQueryConfig => ({
  url: urls.updateFacilitySimulators(facilityId),
  options: {
    ...clubhouseApiHeadersV2(),
    method: "PATCH",
  },
  body,
  transform: (responseJson: Clubhouse_SimulatorResponseDto) => {
    return normalize(responseJson.simulators, [facilityAndSimSchema]).entities;
  },
  update: {
    simulators: (oldValue, newValue): SimulatorsEntitiesState => ({
      ...oldValue,
      ...newValue,
    }),
  },
});

export const updateFacilitySimulatorNameQuery = (
  facilityId: string,
  body: {
    id: string;
    name: string;
  }[],
): AppQueryConfig => ({
  url: urls.updateFacilitySimulatorName(facilityId),
  options: {
    ...clubhouseApiHeadersV2(),
    method: "PUT",
  },
  body,
  transform: (responseJson: SimulatorDto[]) => {
    return normalize(responseJson, [facilityAndSimSchema]).entities;
  },
  update: {
    simulators: (oldValue, newValue): SimulatorsEntitiesState => ({
      ...oldValue,
      ...newValue,
    }),
  },
});
